import { MouseEvent, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { callPricingSchema } from 'utils/schema/functionAndJobSchema'
import {
  MultiSelectDropdown,
  FormLayout,
  Spinner,
  Button,
} from 'components/common'
import useModal from 'hooks/useModal'
import ConfirmRunJobModal from './confirmRunJobModal'
import { fetchStores } from 'utils/api/storeApi'
import { fetchAggregators } from 'utils/api/aggregatorApi'
import {
  ACTIONS,
  Action,
  CallInventoryPriceData,
  getAggregatorsOptions,
  getSelectedStoreAndAggregators,
  getStoreOptions,
} from './utils'
import toast from 'react-hot-toast'
import { callByInventoryJob, callByPriceJob } from 'utils/api/functionAndJobApi'

const PriceAndInventory = () => {
  const [action, setAction] = useState<Action>()

  const [loading, setLoading] = useState<boolean>(true)

  // Store and aggregator dropdown options
  const [storeOptions, setStoreOptions] = useState<any[]>([])
  const [aggregatorOptions, setAggregatorOptions] = useState<any[]>([])

  const [selectedOptions, setSelectedOptions] = useState<any>({
    stores: [],
    aggregators: [],
  })

  const [resetForm, setResetForm] = useState<boolean>(false)

  const { isOpen, closeModal, openModal } = useModal()

  const {
    handleSubmit,
    setValue,
    reset,
    trigger,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<CallInventoryPriceData>({
    resolver: yupResolver(callPricingSchema),
  })

  // fetch store and aggregators to display in dropdown
  const getStoreAndAggreagators = async () => {
    try {
      const [storeData, aggregatorData] = await Promise.all([
        fetchStores({ limit: 200, page: 1, isActive: true }),
        fetchAggregators({ limit: 200, page: 1, isActive: true }),
      ])

      // setting Store Options
      setStoreOptions(getStoreOptions(storeData.data?.docs))

      // setting Aggregators Options
      setAggregatorOptions(getAggregatorsOptions(aggregatorData.data?.docs))
    } catch (error) {
      setStoreOptions([])
      setAggregatorOptions([])
    }
    setLoading(false)
  }

  useEffect(() => {
    getStoreAndAggreagators()
  }, [])

  // Handle final run job and start the job
  const handleConfirm = async () => {
    const actionToast = toast.loading(`Calling ${action}...`, {
      duration: Infinity,
    })

    // getting selceted Stores and aggregators
    const data = getValues()

    try {
      // API: Call the Inventory
      if (action === ACTIONS.INVENTORY) {
        await callByInventoryJob({ storeIds: data.stores })
      }

      // API: Call the Price
      if (action === ACTIONS.PRICE) {
        await callByPriceJob({
          storeIds: data.stores,
          aggregatorIds: data.aggregators,
        })
      }

      toast.success(`Initiated Call ${action}!`, {
        id: actionToast,
        duration: 2500,
      })
    } catch (error) {
      toast.error(`Failed to Initiated call ${action}.`, {
        id: actionToast,
        duration: 2500,
      })
    } finally {
      reset()
      setResetForm((pre) => !pre)
      closeModal()
    }
  }

  // set the selected options to pass in modal and open the modal
  const handleOpenModal = async (data: CallInventoryPriceData) => {
    setSelectedOptions(
      getSelectedStoreAndAggregators(data, storeOptions, aggregatorOptions),
    )
    openModal()
  }

  const handleActions = async ({
    currentTarget,
  }: MouseEvent<HTMLButtonElement>) => {
    // Clears error for default
    clearErrors()

    // Handle CALL BY INVENTORY
    if (ACTIONS.INVENTORY === currentTarget.value) {
      // trigger Stores Validation manually
      const isValid = await trigger('stores')

      // setting action and open modal
      if (isValid) {
        const data = getValues()
        setAction(() => ACTIONS.INVENTORY)

        handleOpenModal({ stores: data.stores, aggregators: [] })
      }
    }

    // Handle CALL BY PRICE
    if (ACTIONS.PRICE === currentTarget.value) {
      // setting action
      setAction(() => ACTIONS.PRICE)

      handleSubmit(handleOpenModal)()
    }
  }

  return (
    <FormLayout>
      <form>
        {/* Modal */}
        <ConfirmRunJobModal
          action={action}
          isOpen={isOpen}
          stores={selectedOptions.stores}
          aggregators={selectedOptions.aggregators}
          closeModal={closeModal}
          handleConfirmAction={handleConfirm}
        />

        {loading ? (
          <Spinner />
        ) : (
          <>
            <h4 className="text-xl font-semibold">Price & Inventory APIs</h4>

            <div className="mt-3 flex max-w-3xl flex-wrap gap-x-16 gap-y-5">
              <div className="w-full max-w-72">
                <MultiSelectDropdown
                  required
                  label="Stores"
                  setValue={setValue}
                  name="stores"
                  error={errors?.stores?.message}
                  includeSelectAll={storeOptions.length > 0}
                  options={
                    storeOptions.length > 0
                      ? storeOptions
                      : [
                          {
                            label: 'No Store Found',
                            value: 'NO_STORE',
                            disabled: true,
                          },
                        ]
                  }
                  reset={resetForm}
                />
              </div>
              <div className="w-full max-w-72">
                <MultiSelectDropdown
                  required
                  label="Aggregators"
                  setValue={setValue}
                  name="aggregators"
                  error={errors?.aggregators?.message}
                  includeSelectAll={aggregatorOptions.length > 0}
                  options={
                    aggregatorOptions?.length > 0
                      ? aggregatorOptions
                      : [
                          {
                            label: 'No Aggregators Found',
                            value: 'NO_AGGREGATORS',
                            disabled: true,
                          },
                        ]
                  }
                  reset={resetForm}
                />
              </div>
            </div>

            {/* Price and Invetory call Button */}
            <div className="mt-8 flex w-full max-w-xs items-center gap-3">
              <Button
                buttonText="Call Inventory"
                className="w-full"
                type="button"
                onClick={handleActions}
                value={ACTIONS.INVENTORY}
              />
              <Button
                buttonText="Call Prices"
                variant="outline"
                className="w-full"
                type="button"
                value={ACTIONS.PRICE}
                onClick={handleActions}
              />
            </div>
          </>
        )}
      </form>
    </FormLayout>
  )
}

export default PriceAndInventory
